// All test definitions organized by category
import {
  validateArraySchema,
  checkReferentialIntegrity,
  findDuplicates,
  dryRunAddReceipt,
  dryRunAddLockChange,
  dryRunAssignCard,
  estimateTokens,
  detectNameVariations,
} from './testHelpers';
import { fetchWorkOrders } from '../appfolioApi';
import { isActiveWO, isCompletedWO } from '../dataQueries';

// ============================================
// TEST CATEGORY: DATA INTEGRITY (Read-Only)
// ============================================
export const dataIntegrityTests = [
  {
    id: 'DI-001',
    name: 'Schema: Users',
    description: 'Verify all users have uid, email, role, status',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { users } = context.data;
      if (!users || users.length === 0) {
        return { status: 'warn', message: 'No users loaded' };
      }

      const result = validateArraySchema(users, ['uid', 'email', 'role', 'status'], 'users');
      if (result.valid) {
        return { status: 'pass', message: `All ${result.validCount} users have valid schema` };
      }
      return {
        status: 'fail',
        message: `${result.invalidCount}/${users.length} users have invalid schema`,
        details: result.errors,
      };
    },
  },
  {
    id: 'DI-002',
    name: 'Schema: Cards',
    description: 'Verify all cards have cardLast4, status',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { cards } = context.data;
      if (!cards || cards.length === 0) {
        return { status: 'warn', message: 'No cards loaded' };
      }

      const result = validateArraySchema(cards, ['cardLast4', 'status'], 'cards');
      if (result.valid) {
        return { status: 'pass', message: `All ${result.validCount} cards have valid schema` };
      }
      return {
        status: 'fail',
        message: `${result.invalidCount}/${cards.length} cards have invalid schema`,
        details: result.errors,
      };
    },
  },
  {
    id: 'DI-003',
    name: 'Schema: Work Orders',
    description: 'Verify WOs have id, description, status',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders } = context.data;
      if (!workOrders || workOrders.length === 0) {
        return { status: 'warn', message: 'No work orders loaded' };
      }

      const result = validateArraySchema(workOrders, ['id', 'description', 'status'], 'workOrders');
      if (result.valid) {
        return { status: 'pass', message: `All ${result.validCount} WOs have valid schema` };
      }
      return {
        status: 'warn',
        message: `${result.invalidCount}/${workOrders.length} WOs have incomplete schema`,
        details: result.errors,
      };
    },
  },
  {
    id: 'DI-004',
    name: 'Referential: Card Assignments',
    description: 'Check that assignedCards reference valid cards',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { users, cards } = context.data;
      if (!users || !cards) {
        return { status: 'warn', message: 'Users or cards not loaded' };
      }

      const validCardNumbers = new Set(cards.map(c => c.cardLast4));
      const invalidAssignments = [];

      users.forEach(user => {
        const assigned = user.assignedCards || [];
        assigned.forEach(cardLast4 => {
          if (!validCardNumbers.has(cardLast4)) {
            if (invalidAssignments.length < 10) {
              invalidAssignments.push({
                user: user.displayName || user.email,
                card: cardLast4,
              });
            }
          }
        });
      });

      if (invalidAssignments.length === 0) {
        return { status: 'pass', message: 'All card assignments reference valid cards' };
      }
      return {
        status: 'fail',
        message: `${invalidAssignments.length} invalid card assignments found`,
        details: invalidAssignments.map(a => `${a.user} -> ${a.card}`),
      };
    },
  },
  {
    id: 'DI-005',
    name: 'Referential: Receipts',
    description: 'Check receipt.woId references valid WOs',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { receipts, workOrders } = context.data;
      if (!receipts || receipts.length === 0) {
        return { status: 'pass', message: 'No receipts to validate' };
      }
      if (!workOrders) {
        return { status: 'warn', message: 'Work orders not loaded' };
      }

      const result = checkReferentialIntegrity(receipts, workOrders, 'woId', 'id');
      if (result.valid) {
        return { status: 'pass', message: `All ${receipts.length} receipts reference valid WOs` };
      }
      return {
        status: 'warn',
        message: `${result.orphanCount} receipts reference non-existent WOs`,
        details: result.orphans.slice(0, 5).map(o => `Receipt ${o.itemId} -> WO ${o.missingRef}`),
      };
    },
  },
  {
    id: 'DI-006',
    name: 'Orphan Detection: Lock Changes',
    description: 'Find lock changes with invalid unit references',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { lockChanges, units } = context.data;
      if (!lockChanges || lockChanges.length === 0) {
        return { status: 'pass', message: 'No lock changes to validate' };
      }
      if (!units || units.length === 0) {
        return { status: 'warn', message: 'Units not loaded' };
      }

      const validUnitIds = new Set(units.map(u => String(u.id || u.unitId)));
      const orphans = lockChanges.filter(lc => !validUnitIds.has(String(lc.unitId)));

      if (orphans.length === 0) {
        return { status: 'pass', message: `All ${lockChanges.length} lock changes reference valid units` };
      }
      return {
        status: 'warn',
        message: `${orphans.length}/${lockChanges.length} lock changes reference unknown units`,
        details: orphans.slice(0, 5).map(o => `Lock change for unit ${o.unitId}`),
      };
    },
  },
  {
    id: 'DI-007',
    name: 'Duplicate Detection: Card Numbers',
    description: 'Find duplicate card numbers',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { cards } = context.data;
      if (!cards || cards.length === 0) {
        return { status: 'pass', message: 'No cards to check' };
      }

      const result = findDuplicates(cards, 'cardLast4');
      if (!result.hasDuplicates) {
        return { status: 'pass', message: `All ${cards.length} card numbers are unique` };
      }
      return {
        status: 'fail',
        message: `${result.duplicates.length} duplicate card numbers found`,
        details: result.duplicates.map(d => `Card ${d.value}: ${d.count} occurrences`),
      };
    },
  },
  {
    id: 'DI-008',
    name: 'Schema: Inspections',
    description: 'Verify inspections have required fields',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { inspections } = context.data;
      if (!inspections || inspections.length === 0) {
        return { status: 'pass', message: 'No inspections to validate' };
      }

      const result = validateArraySchema(inspections, ['unitId'], 'inspections');
      if (result.valid) {
        return { status: 'pass', message: `All ${result.validCount} inspections have valid schema` };
      }
      return {
        status: 'warn',
        message: `${result.invalidCount}/${inspections.length} inspections have incomplete schema`,
        details: result.errors,
      };
    },
  },
  {
    id: 'DI-009',
    name: 'Schema: Lock Codes',
    description: 'Verify lock codes have unitId',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { lockCodes } = context.data;
      if (!lockCodes || lockCodes.length === 0) {
        return { status: 'warn', message: 'No lock codes loaded' };
      }

      const result = validateArraySchema(lockCodes, ['unitId'], 'lockCodes');
      if (result.valid) {
        return { status: 'pass', message: `All ${result.validCount} lock codes have valid schema` };
      }
      return {
        status: 'warn',
        message: `${result.invalidCount}/${lockCodes.length} lock codes missing unitId`,
        details: result.errors,
      };
    },
  },
  {
    id: 'DI-010',
    name: 'Duplicate Detection: User Emails',
    description: 'Find duplicate user emails',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { users } = context.data;
      if (!users || users.length === 0) {
        return { status: 'pass', message: 'No users to check' };
      }

      const result = findDuplicates(users, 'email');
      if (!result.hasDuplicates) {
        return { status: 'pass', message: `All ${users.length} user emails are unique` };
      }
      return {
        status: 'fail',
        message: `${result.duplicates.length} duplicate emails found`,
        details: result.duplicates.map(d => `${d.value}: ${d.count} occurrences`),
      };
    },
  },
  {
    id: 'DI-011',
    name: 'WOs Missing unitId',
    description: 'Per data model, unitId is required on all work orders',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders } = context.data;
      if (!workOrders || workOrders.length === 0) {
        return { status: 'warn', message: 'No work orders loaded' };
      }

      const missing = workOrders.filter(wo => !wo.unitId);
      if (missing.length === 0) {
        return { status: 'pass', message: `All ${workOrders.length} WOs have unitId` };
      }
      return {
        status: 'fail',
        message: `${missing.length}/${workOrders.length} WOs missing unitId`,
        details: missing.slice(0, 5).map(wo => `WO ${wo.id}: unit="${wo.unit || 'none'}", unitId=missing`),
      };
    },
  },
  {
    id: 'DI-012',
    name: 'WOs Missing propertyId',
    description: 'Per data model, propertyId is required on all work orders',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders } = context.data;
      if (!workOrders || workOrders.length === 0) {
        return { status: 'warn', message: 'No work orders loaded' };
      }

      const missing = workOrders.filter(wo => !wo.propertyId);
      if (missing.length === 0) {
        return { status: 'pass', message: `All ${workOrders.length} WOs have propertyId` };
      }
      return {
        status: 'fail',
        message: `${missing.length}/${workOrders.length} WOs missing propertyId`,
        details: missing.slice(0, 5).map(wo => `WO ${wo.id}: property="${wo.property || 'none'}", propertyId=missing`),
      };
    },
  },
  {
    id: 'DI-013',
    name: 'LockCodes vs Units Cross-Ref',
    description: 'Units in lockCodes but not units collection, and vice versa',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { lockCodes, units } = context.data;
      if (!lockCodes || !units) {
        return { status: 'warn', message: 'LockCodes or units not loaded' };
      }

      const lockCodeUnitIds = new Set(lockCodes.map(lc => String(lc.unitId)).filter(Boolean));
      const unitIds = new Set(units.map(u => String(u.unitId || u.id)).filter(Boolean));

      const inLockNotUnits = [...lockCodeUnitIds].filter(id => !unitIds.has(id));
      const inUnitsNotLock = [...unitIds].filter(id => !lockCodeUnitIds.has(id));

      const details = [];
      if (inLockNotUnits.length > 0) {
        details.push(`${inLockNotUnits.length} in lockCodes but NOT in units: ${inLockNotUnits.slice(0, 3).join(', ')}${inLockNotUnits.length > 3 ? '...' : ''}`);
      }
      if (inUnitsNotLock.length > 0) {
        details.push(`${inUnitsNotLock.length} in units but NOT in lockCodes: ${inUnitsNotLock.slice(0, 3).join(', ')}${inUnitsNotLock.length > 3 ? '...' : ''}`);
      }

      if (inLockNotUnits.length === 0 && inUnitsNotLock.length === 0) {
        return { status: 'pass', message: `${lockCodeUnitIds.size} lockCodes match ${unitIds.size} units perfectly` };
      }
      return {
        status: 'warn',
        message: `lockCodes: ${lockCodeUnitIds.size}, units: ${unitIds.size} — mismatches found`,
        details,
      };
    },
  },
  {
    id: 'DI-014',
    name: 'Orphaned WO References',
    description: 'WOs with propertyId/unitId that dont exist in properties/units',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders, units, properties } = context.data;
      if (!workOrders?.length) {
        return { status: 'warn', message: 'No work orders loaded' };
      }

      const details = [];

      if (units?.length) {
        const unitIds = new Set(units.map(u => String(u.unitId || u.id)));
        const orphanedUnits = workOrders.filter(wo => wo.unitId && !unitIds.has(String(wo.unitId)));
        if (orphanedUnits.length > 0) {
          details.push(`${orphanedUnits.length} WOs reference unitIds not in units collection`);
          orphanedUnits.slice(0, 3).forEach(wo => details.push(`  WO ${wo.id} -> unitId "${wo.unitId}"`));
        }
      }

      if (properties?.length) {
        const propIds = new Set(properties.map(p => String(p.propertyId || p.id)));
        const orphanedProps = workOrders.filter(wo => wo.propertyId && !propIds.has(String(wo.propertyId)));
        if (orphanedProps.length > 0) {
          details.push(`${orphanedProps.length} WOs reference propertyIds not in properties collection`);
          orphanedProps.slice(0, 3).forEach(wo => details.push(`  WO ${wo.id} -> propertyId "${wo.propertyId}"`));
        }
      }

      if (details.length === 0) {
        return { status: 'pass', message: 'All WO unit/property references are valid' };
      }
      return { status: 'warn', message: 'Some WOs reference non-existent units/properties', details };
    },
  },
  {
    id: 'DI-015',
    name: 'Tech Name Consistency',
    description: 'Detect same tech with different name spellings across collections',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders, laborEntries } = context.data;
      const names = [];

      if (workOrders?.length) {
        workOrders.forEach(wo => { if (wo.tech) names.push(wo.tech); });
      }
      if (laborEntries?.length) {
        laborEntries.forEach(le => { if (le.techName) names.push(le.techName); });
      }

      if (names.length === 0) {
        return { status: 'warn', message: 'No tech names found to analyze' };
      }

      const variations = detectNameVariations(names);
      if (variations.length === 0) {
        const unique = new Set(names.map(n => n.toLowerCase().trim()));
        return { status: 'pass', message: `${unique.size} unique tech names, no case/spelling variations detected` };
      }
      return {
        status: 'warn',
        message: `${variations.length} tech name variation(s) found`,
        details: variations.map(v => `"${v.normalized}": ${v.variations.join(', ')}`),
      };
    },
  },
  {
    id: 'DI-016',
    name: 'Duplicate WO IDs',
    description: 'Check for duplicate work order IDs',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders } = context.data;
      if (!workOrders || workOrders.length === 0) {
        return { status: 'pass', message: 'No work orders to check' };
      }

      const result = findDuplicates(workOrders, 'id');
      if (!result.hasDuplicates) {
        return { status: 'pass', message: `All ${workOrders.length} WO IDs are unique` };
      }
      return {
        status: 'fail',
        message: `${result.duplicates.length} duplicate WO IDs found`,
        details: result.duplicates.map(d => `WO ${d.value}: ${d.count} occurrences`),
      };
    },
  },
];

// ============================================
// TEST CATEGORY: FUNCTION EXECUTION (Read-Only)
// ============================================
export const functionExecutionTests = [
  {
    id: 'FE-001',
    name: 'getCurrentTech()',
    description: 'Returns valid tech object with name',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getCurrentTech } = context.data;
      if (typeof getCurrentTech !== 'function') {
        return { status: 'fail', message: 'getCurrentTech is not a function' };
      }

      const tech = getCurrentTech();
      if (!tech) {
        return { status: 'warn', message: 'No tech matched for current user' };
      }
      if (!tech.name) {
        return { status: 'fail', message: 'Tech object missing name field' };
      }
      return { status: 'pass', message: `Matched tech: ${tech.name}` };
    },
  },
  {
    id: 'FE-002',
    name: 'getOpenWOsForTech()',
    description: 'Returns array of open work orders',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getOpenWOsForTech, getCurrentTech } = context.data;
      if (typeof getOpenWOsForTech !== 'function') {
        return { status: 'fail', message: 'getOpenWOsForTech is not a function' };
      }

      const tech = getCurrentTech?.();
      const openWOs = getOpenWOsForTech(tech?.name);

      if (!Array.isArray(openWOs)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      // Verify all returned WOs have an active status
      const nonActive = openWOs.filter(wo => !isActiveWO(wo));
      if (nonActive.length > 0) {
        return { status: 'fail', message: `${nonActive.length} WOs returned are not active status` };
      }

      return { status: 'pass', message: `Found ${openWOs.length} open WOs for ${tech?.name || 'current tech'}` };
    },
  },
  {
    id: 'FE-003',
    name: 'getCardsForUser()',
    description: 'Returns cards (explicit or inferred)',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getCardsForUser } = context.data;
      if (typeof getCardsForUser !== 'function') {
        return { status: 'fail', message: 'getCardsForUser is not a function' };
      }

      const cards = getCardsForUser();
      if (!Array.isArray(cards)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      const source = context.auth.userProfile?.assignedCards?.length > 0
        ? 'explicit assignment'
        : 'name inference';

      return {
        status: 'pass',
        message: `Found ${cards.length} card(s) via ${source}`,
        details: cards.map(c => c.cardLast4),
      };
    },
  },
  {
    id: 'FE-004',
    name: 'getAvailableCards()',
    description: 'Returns unassigned active cards',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getAvailableCards, cards } = context.data;
      if (typeof getAvailableCards !== 'function') {
        return { status: 'fail', message: 'getAvailableCards is not a function' };
      }

      const available = getAvailableCards();
      if (!Array.isArray(available)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      // Verify all returned cards are active
      const nonActive = available.filter(c => c.status !== 'active');
      if (nonActive.length > 0) {
        return { status: 'warn', message: `${nonActive.length} available cards are not active` };
      }

      return {
        status: 'pass',
        message: `${available.length}/${cards?.length || 0} cards available (unassigned + active)`,
      };
    },
  },
  {
    id: 'FE-005',
    name: 'getLockCode(unitId)',
    description: 'Returns lock code for valid unit',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getLockCode, lockCodes, units } = context.data;
      if (typeof getLockCode !== 'function') {
        return { status: 'fail', message: 'getLockCode is not a function' };
      }

      // Find a unit that should have a lock code
      const testUnitId = lockCodes?.[0]?.unitId || units?.[0]?.unitId || units?.[0]?.id;
      if (!testUnitId) {
        return { status: 'warn', message: 'No units available to test' };
      }

      const lockCode = getLockCode(testUnitId);
      if (!lockCode) {
        return { status: 'warn', message: `No lock code found for unit ${testUnitId}` };
      }

      return { status: 'pass', message: `Lock code found for unit ${testUnitId}` };
    },
  },
  {
    id: 'FE-006',
    name: 'getWOsForUnit(unitId)',
    description: 'Returns WOs and warns if >50',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getWOsForUnit, workOrders, units } = context.data;
      if (typeof getWOsForUnit !== 'function') {
        return { status: 'fail', message: 'getWOsForUnit is not a function' };
      }

      // Find a unit with WOs - MUST use unitId (canonical identifier)
      const testUnitId = workOrders?.[0]?.unitId;
      if (!testUnitId) {
        return { status: 'fail', message: 'No work orders have unitId field - data import issue' };
      }

      const wos = getWOsForUnit(testUnitId);
      if (!Array.isArray(wos)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      if (wos.length > 50) {
        return {
          status: 'warn',
          message: `Unit ${testUnitId} has ${wos.length} WOs - may cause token overflow in AI`,
        };
      }

      return { status: 'pass', message: `Unit ${testUnitId} has ${wos.length} WOs` };
    },
  },
  {
    id: 'FE-006b',
    name: 'Unit filtering integrity check',
    description: 'Verifies getWOsForUnit returns ONLY matching WOs and identifies data issues',
    destructive: false,
    aiCost: false,
    timeout: 10000,
    run: async (context) => {
      const { getWOsForUnit, workOrders } = context.data;
      if (!workOrders?.length) {
        return { status: 'warn', message: 'No work orders to test' };
      }

      const details = [];

      // 1. Check field population
      const withUnitId = workOrders.filter(wo => wo.unitId).length;
      const withUnit = workOrders.filter(wo => wo.unit).length;
      const withBoth = workOrders.filter(wo => wo.unitId && wo.unit).length;
      const withNeither = workOrders.filter(wo => !wo.unitId && !wo.unit).length;

      details.push(`Field population: ${withUnitId}/${workOrders.length} have unitId, ${withUnit}/${workOrders.length} have unit, ${withNeither} have neither`);

      if (withNeither > 0) {
        details.push(`WARNING: ${withNeither} WOs have no unit identifier at all!`);
      }

      // 2. Check for inconsistent unit naming
      const unitValues = new Map(); // unit value -> count
      workOrders.forEach(wo => {
        const unitKey = wo.unit || wo.unitId || 'EMPTY';
        unitValues.set(unitKey, (unitValues.get(unitKey) || 0) + 1);
      });

      // 3. Test filtering accuracy - pick a random WO and verify filter returns ONLY matching WOs
      const testWO = workOrders[Math.floor(Math.random() * workOrders.length)];
      const filterInput = testWO.unitId; // MUST use unitId (canonical identifier)
      if (!filterInput) {
        details.push(`CRITICAL: Test WO ${testWO.id} is missing unitId field`);
        return { status: 'fail', message: 'WOs missing unitId - cannot verify filtering', details };
      }
      const filtered = getWOsForUnit(filterInput);

      // Verify all returned WOs actually match
      const falsePositives = filtered.filter(wo => wo.unit !== filterInput && wo.unitId !== filterInput);
      if (falsePositives.length > 0) {
        details.push(`FILTER BUG: ${falsePositives.length} WOs returned don't match unit "${filterInput}"`);
        details.push(`False positive example: unit="${falsePositives[0]?.unit}", unitId="${falsePositives[0]?.unitId}"`);
      }

      // 4. Check if any WOs that SHOULD match were missed
      const shouldMatch = workOrders.filter(wo => wo.unit === filterInput || wo.unitId === filterInput);
      const falseNegatives = shouldMatch.length - filtered.length;
      if (falseNegatives > 0) {
        details.push(`FILTER BUG: ${falseNegatives} WOs should match but weren't returned`);
      }

      // 5. Estimate token impact - find units with most WOs
      const woCountByUnit = new Map();
      workOrders.forEach(wo => {
        const key = wo.unit || wo.unitId || 'EMPTY';
        woCountByUnit.set(key, (woCountByUnit.get(key) || 0) + 1);
      });
      const sortedUnits = [...woCountByUnit.entries()].sort((a, b) => b[1] - a[1]);
      const topUnits = sortedUnits.slice(0, 3);
      details.push(`Highest WO counts: ${topUnits.map(([u, c]) => `${u}(${c})`).join(', ')}`);

      // 6. Check for empty string matching issue
      const emptyMatches = getWOsForUnit('');
      if (emptyMatches.length > 0) {
        details.push(`BUG: Empty string filter returned ${emptyMatches.length} WOs (should be 0)`);
      }

      // Determine status
      const hasBugs = falsePositives.length > 0 || falseNegatives > 0 || emptyMatches.length > 0 || withNeither > 0;
      const hasWarnings = topUnits.some(([, count]) => count > 50);

      if (hasBugs) {
        return {
          status: 'fail',
          message: `Unit filtering has data integrity issues`,
          details,
        };
      }

      if (hasWarnings) {
        return {
          status: 'warn',
          message: `Filtering works but some units have >50 WOs (token overflow risk)`,
          details,
        };
      }

      return {
        status: 'pass',
        message: `Unit filtering verified: ${unitValues.size} unique units, filter accurate`,
        details,
      };
    },
  },
  {
    id: 'FE-006c',
    name: 'getWOsForProperty(propertyId)',
    description: 'Returns WOs for a property using propertyId (canonical identifier)',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getWOsForProperty, workOrders } = context.data;
      if (typeof getWOsForProperty !== 'function') {
        return { status: 'fail', message: 'getWOsForProperty is not a function' };
      }

      // Find a property with WOs - MUST use propertyId (canonical identifier)
      const testPropertyId = workOrders?.[0]?.propertyId;
      if (!testPropertyId) {
        return { status: 'fail', message: 'No work orders have propertyId field - data import issue' };
      }

      const wos = getWOsForProperty(testPropertyId);
      if (!Array.isArray(wos)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      // Verify all returned WOs actually match the propertyId
      const falsePositives = wos.filter(wo => wo.propertyId !== testPropertyId);
      if (falsePositives.length > 0) {
        return {
          status: 'fail',
          message: `Filter returned ${falsePositives.length} WOs that don't match propertyId`,
          details: [`Example: WO ${falsePositives[0]?.id} has propertyId="${falsePositives[0]?.propertyId}"`],
        };
      }

      // Check field population
      const withPropertyId = workOrders.filter(wo => wo.propertyId).length;
      const missingPropertyId = workOrders.length - withPropertyId;

      if (missingPropertyId > 0) {
        return {
          status: 'warn',
          message: `${missingPropertyId}/${workOrders.length} WOs missing propertyId field`,
          details: [`Property ${testPropertyId} has ${wos.length} WOs`],
        };
      }

      if (wos.length > 200) {
        return {
          status: 'warn',
          message: `Property ${testPropertyId} has ${wos.length} WOs - may cause token overflow`,
        };
      }

      return { status: 'pass', message: `Property ${testPropertyId} has ${wos.length} WOs, filter accurate` };
    },
  },
  {
    id: 'FE-007',
    name: 'getUnmatchedBankCharges()',
    description: 'Returns charges without receipts',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getUnmatchedBankCharges, bankCharges } = context.data;
      if (typeof getUnmatchedBankCharges !== 'function') {
        return { status: 'fail', message: 'getUnmatchedBankCharges is not a function' };
      }

      const unmatched = getUnmatchedBankCharges(false); // all, not just current tech
      if (!Array.isArray(unmatched)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      const totalCharges = bankCharges?.length || 0;
      const matchedCount = totalCharges - unmatched.length;

      return {
        status: 'pass',
        message: `${unmatched.length} unmatched of ${totalCharges} total (${matchedCount} matched)`,
      };
    },
  },
  {
    id: 'FE-008',
    name: 'getAllUsers()',
    description: 'Returns sorted user list',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getAllUsers } = context.data;
      if (typeof getAllUsers !== 'function') {
        return { status: 'fail', message: 'getAllUsers is not a function' };
      }

      const users = getAllUsers();
      if (!Array.isArray(users)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      // Check if sorted (active first)
      let sorted = true;
      for (let i = 1; i < users.length; i++) {
        if (users[i].status === 'active' && users[i-1].status !== 'active') {
          sorted = false;
          break;
        }
      }

      if (!sorted) {
        return { status: 'warn', message: `${users.length} users returned but not sorted by status` };
      }

      return { status: 'pass', message: `${users.length} users returned, sorted by status` };
    },
  },
  {
    id: 'FE-009',
    name: 'getAllCards()',
    description: 'Returns sorted card list',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getAllCards } = context.data;
      if (typeof getAllCards !== 'function') {
        return { status: 'fail', message: 'getAllCards is not a function' };
      }

      const cards = getAllCards();
      if (!Array.isArray(cards)) {
        return { status: 'fail', message: 'Did not return an array' };
      }

      return { status: 'pass', message: `${cards.length} cards returned` };
    },
  },
];

// ============================================
// TEST CATEGORY: PERMISSION ENFORCEMENT
// ============================================
export const permissionTests = [
  {
    id: 'PE-001',
    name: 'canWrite() Check',
    description: 'Verify canWrite returns correct value',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { canWrite, isDemo, userProfile } = context.auth;

      if (typeof canWrite !== 'function') {
        return { status: 'fail', message: 'canWrite is not a function' };
      }

      const writeAllowed = canWrite();
      const demoUser = typeof isDemo === 'function' ? isDemo() : false;
      const role = userProfile?.role;

      // GOD_MODE should always allow write
      const isGodMode = ['aj@snshn.co'].includes(userProfile?.email?.toLowerCase());

      if (isGodMode) {
        if (!writeAllowed) {
          return { status: 'fail', message: 'GOD_MODE user should have canWrite=true' };
        }
        return { status: 'pass', message: 'GOD_MODE bypasses all restrictions' };
      }

      if (demoUser && writeAllowed) {
        return { status: 'fail', message: 'Demo user should have canWrite=false' };
      }

      if (!demoUser && !writeAllowed && role !== 'demo') {
        return { status: 'fail', message: `Non-demo user (${role}) should have canWrite=true` };
      }

      return {
        status: 'pass',
        message: `canWrite=${writeAllowed} correct for role=${role}`,
      };
    },
  },
  {
    id: 'PE-002',
    name: 'isAdmin() Check',
    description: 'Verify admin detection',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { isAdmin, userProfile } = context.auth;

      if (typeof isAdmin !== 'function') {
        return { status: 'fail', message: 'isAdmin is not a function' };
      }

      const adminResult = isAdmin();
      const role = userProfile?.role;
      const isGodMode = ['aj@snshn.co'].includes(userProfile?.email?.toLowerCase());

      if (isGodMode && !adminResult) {
        return { status: 'fail', message: 'GOD_MODE user should be admin' };
      }

      if (role === 'admin' && !adminResult && !isGodMode) {
        return { status: 'fail', message: 'Role is admin but isAdmin()=false' };
      }

      return { status: 'pass', message: `isAdmin=${adminResult} for role=${role}` };
    },
  },
  {
    id: 'PE-003',
    name: 'isPM() Check',
    description: 'Verify PM detection',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { isPM, userProfile } = context.auth;

      if (typeof isPM !== 'function') {
        return { status: 'fail', message: 'isPM is not a function' };
      }

      const pmResult = isPM();
      const role = userProfile?.role;
      const shouldBePM = ['pm', 'admin'].includes(role) ||
                         ['aj@snshn.co'].includes(userProfile?.email?.toLowerCase());

      if (shouldBePM !== pmResult) {
        return { status: 'fail', message: `Expected isPM=${shouldBePM} for role=${role}` };
      }

      return { status: 'pass', message: `isPM=${pmResult} for role=${role}` };
    },
  },
  {
    id: 'PE-004',
    name: 'isDemo() Check',
    description: 'Verify demo detection (GOD_MODE override)',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { isDemo, userProfile } = context.auth;

      if (typeof isDemo !== 'function') {
        return { status: 'fail', message: 'isDemo is not a function' };
      }

      const demoResult = isDemo();
      const role = userProfile?.role;
      const isGodMode = ['aj@snshn.co'].includes(userProfile?.email?.toLowerCase());

      // GOD_MODE should NEVER be demo
      if (isGodMode && demoResult) {
        return { status: 'fail', message: 'GOD_MODE user should never be demo' };
      }

      // Non-GOD_MODE demo role should be demo
      if (!isGodMode && role === 'demo' && !demoResult) {
        return { status: 'fail', message: 'Demo role should have isDemo=true' };
      }

      return { status: 'pass', message: `isDemo=${demoResult} for role=${role}` };
    },
  },
  {
    id: 'PE-005',
    name: 'Demo User Write Block',
    description: 'Verify demo users cannot write',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { isDemo, canWrite, userProfile } = context.auth;
      const isGodMode = ['aj@snshn.co'].includes(userProfile?.email?.toLowerCase());

      if (isGodMode) {
        return { status: 'pass', message: 'GOD_MODE user - write always allowed' };
      }

      const demoUser = typeof isDemo === 'function' && isDemo();
      const writeAllowed = typeof canWrite === 'function' && canWrite();

      if (demoUser && writeAllowed) {
        return { status: 'fail', message: 'Demo user should not have write access' };
      }

      if (demoUser) {
        return { status: 'pass', message: 'Demo user correctly blocked from writes' };
      }

      return { status: 'pass', message: 'Non-demo user has write access' };
    },
  },
  {
    id: 'PE-006',
    name: 'GOD_MODE Bypass',
    description: 'Verify GOD_MODE overrides demo restrictions',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { userProfile, canWrite } = context.auth;
      const email = userProfile?.email?.toLowerCase();
      const isGodMode = ['aj@snshn.co'].includes(email);

      if (!isGodMode) {
        return { status: 'pass', message: `${email} is not GOD_MODE` };
      }

      const writeAllowed = typeof canWrite === 'function' && canWrite();
      if (!writeAllowed) {
        return { status: 'fail', message: 'GOD_MODE should have write access' };
      }

      return { status: 'pass', message: `GOD_MODE active for ${email}` };
    },
  },
  {
    id: 'PE-007',
    name: 'hasRole() Function',
    description: 'Verify role checking works',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { hasRole, userProfile } = context.auth;

      if (typeof hasRole !== 'function') {
        return { status: 'fail', message: 'hasRole is not a function' };
      }

      const currentRole = userProfile?.role;
      if (!currentRole) {
        return { status: 'warn', message: 'No role set on user profile' };
      }

      const hasCurrentRole = hasRole(currentRole);
      if (!hasCurrentRole) {
        return { status: 'fail', message: `hasRole("${currentRole}") should be true` };
      }

      // Test array variant
      const hasOneOf = hasRole(['admin', 'pm', 'tech', 'demo']);
      if (!hasOneOf) {
        return { status: 'fail', message: 'hasRole with array should match' };
      }

      return { status: 'pass', message: `hasRole works for ${currentRole}` };
    },
  },
  {
    id: 'PE-008',
    name: 'Profile Data Completeness',
    description: 'Verify user profile has all expected fields',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { userProfile } = context.auth;

      if (!userProfile) {
        return { status: 'fail', message: 'No user profile loaded' };
      }

      const requiredFields = ['uid', 'email', 'role', 'status'];
      const optionalFields = ['displayName', 'assignedCards', 'phone', 'photoUrl'];

      const missing = requiredFields.filter(f => !userProfile[f]);
      if (missing.length > 0) {
        return { status: 'fail', message: `Profile missing: ${missing.join(', ')}` };
      }

      const presentOptional = optionalFields.filter(f => userProfile[f]);
      return {
        status: 'pass',
        message: `Profile complete. Optional fields: ${presentOptional.length}/${optionalFields.length}`,
      };
    },
  },
];

// ============================================
// TEST CATEGORY: WORKFLOW DRY-RUN TESTS
// ============================================
export const workflowTests = [
  {
    id: 'WF-001',
    name: 'Dry-run: addReceipt()',
    description: 'Validate receipt would be created',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders, canWrite } = context.data;

      const testWO = workOrders?.[0];
      if (!testWO) {
        return { status: 'warn', message: 'No work orders available for test' };
      }

      const testData = {
        woId: testWO.id,
        photoUrl: 'https://example.com/test.jpg',
        amount: 100,
      };

      const result = dryRunAddReceipt(testData, { workOrders, canWrite });
      return result;
    },
  },
  {
    id: 'WF-002',
    name: 'Dry-run: addLockChange()',
    description: 'Validate lock change would update both collections',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { lockCodes, canWrite, units } = context.data;

      const testUnitId = lockCodes?.[0]?.unitId || units?.[0]?.unitId || '12345';

      const testData = {
        unitId: testUnitId,
        newLock: '9999',
        previousLock: '1234',
      };

      const result = dryRunAddLockChange(testData, { lockCodes, canWrite });
      return result;
    },
  },
  {
    id: 'WF-003',
    name: 'Dry-run: assignCardToSelf()',
    description: 'Validate card assignment would succeed',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { cards, users, canWrite, getAvailableCards } = context.data;
      const { userProfile } = context.auth;

      // Get an available card
      const availableCards = typeof getAvailableCards === 'function' ? getAvailableCards() : [];
      const testCard = availableCards[0]?.cardLast4;

      if (!testCard) {
        return { status: 'warn', message: 'No available cards to test assignment' };
      }

      const result = dryRunAssignCard(testCard, { cards, users, userProfile, canWrite });
      return result;
    },
  },
  {
    id: 'WF-004',
    name: 'Dry-run: addInspection()',
    description: 'Validate inspection would be created',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { canWrite, units } = context.data;

      if (!canWrite || !canWrite()) {
        return { status: 'fail', message: 'No write access (demo user)' };
      }

      const testUnitId = units?.[0]?.unitId || units?.[0]?.id || '12345';

      return {
        status: 'pass',
        message: `Inspection for unit ${testUnitId} would be created`,
      };
    },
  },
  {
    id: 'WF-005',
    name: 'Dry-run: addConsultSession()',
    description: 'Validate consult session would be saved',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { canWrite, workOrders } = context.data;

      if (!canWrite || !canWrite()) {
        return { status: 'fail', message: 'No write access (demo user)' };
      }

      const testWO = workOrders?.[0];
      if (!testWO) {
        return { status: 'warn', message: 'No work orders available' };
      }

      return {
        status: 'pass',
        message: `Consult session for WO ${testWO.id} would be saved`,
      };
    },
  },
  {
    id: 'WF-006',
    name: 'Data Refresh',
    description: 'Verify refresh function exists and is callable',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { refreshDataFromStorage } = context.data;

      if (typeof refreshDataFromStorage !== 'function') {
        return { status: 'fail', message: 'refreshDataFromStorage is not a function' };
      }

      // Don't actually call it, just verify it exists
      return { status: 'pass', message: 'Refresh function available' };
    },
  },
];

// ============================================
// TEST CATEGORY: AI OPERATIONS (Optional - Cost)
// ============================================
export const aiTests = [
  {
    id: 'AI-001',
    name: 'Token Estimation',
    description: 'Verify estimateTokens calculation',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async () => {
      const testMessages = [
        { role: 'system', content: 'You are a helpful assistant.' },
        { role: 'user', content: 'Hello, can you help me with a maintenance issue?' },
      ];

      const estimated = estimateTokens(testMessages);
      const expectedRange = [10, 30]; // Rough range

      if (estimated < expectedRange[0] || estimated > expectedRange[1]) {
        return {
          status: 'warn',
          message: `Token estimate ${estimated} outside expected range ${expectedRange.join('-')}`,
        };
      }

      return { status: 'pass', message: `Estimated ${estimated} tokens` };
    },
  },
  {
    id: 'AI-002',
    name: 'MAX_HISTORY_ITEMS Check',
    description: 'Verify history limiting is enforced',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async () => {
      // Reference the constant from openai.js (hardcoded here for testing)
      const MAX_HISTORY_ITEMS = 25;

      // Simulate a large history
      const largeHistory = Array(100).fill().map((_, i) => ({
        id: `WO-${i}`,
        description: `Test work order ${i}`,
        createdDate: '2024-01-01',
      }));

      const limited = largeHistory.slice(0, MAX_HISTORY_ITEMS);

      if (limited.length !== MAX_HISTORY_ITEMS) {
        return { status: 'fail', message: 'History limiting not working' };
      }

      return {
        status: 'pass',
        message: `History limited to ${MAX_HISTORY_ITEMS} items (was ${largeHistory.length})`,
      };
    },
  },
  {
    id: 'AI-003',
    name: 'OpenAI API Key',
    description: 'Check if API key is configured',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async () => {
      const apiKey = import.meta.env.VITE_OPENAI_API_KEY;

      if (!apiKey) {
        return { status: 'fail', message: 'VITE_OPENAI_API_KEY not set' };
      }

      if (!apiKey.startsWith('sk-')) {
        return { status: 'warn', message: 'API key format looks invalid' };
      }

      return { status: 'pass', message: 'API key configured' };
    },
  },
  {
    id: 'AI-004',
    name: 'generateWOBriefing (Live)',
    description: 'Call AI API with test data',
    destructive: false,
    aiCost: true, // This costs money
    timeout: 30000,
    run: async (context) => {
      const { workOrders } = context.data;

      // Dynamic import to avoid loading openai.js if not needed
      const { generateWOBriefing } = await import('../openai');

      const testWO = workOrders?.[0];
      if (!testWO) {
        return { status: 'warn', message: 'No work orders available' };
      }

      try {
        const result = await generateWOBriefing(testWO, [], []);
        if (!result || (typeof result === 'object' && !result.content)) {
          return { status: 'fail', message: 'Empty response from AI' };
        }
        return {
          status: 'pass',
          message: 'AI responded successfully',
          details: [(result.content || result).substring(0, 100) + '...'],
        };
      } catch (error) {
        return { status: 'fail', message: `API error: ${error.message}` };
      }
    },
  },
];

// ============================================
// TEST CATEGORY: STORAGE & OFFLINE
// ============================================
export const storageTests = [
  {
    id: 'ST-001',
    name: 'Firebase Storage Connection',
    description: 'Verify storage ref can be created',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async () => {
      try {
        const { ref } = await import('firebase/storage');
        const { storage } = await import('../../config/firebase');

        const testRef = ref(storage, 'test/connection-check');
        if (!testRef) {
          return { status: 'fail', message: 'Could not create storage ref' };
        }
        return { status: 'pass', message: 'Storage ref created successfully' };
      } catch (error) {
        return { status: 'fail', message: `Storage error: ${error.message}` };
      }
    },
  },
  {
    id: 'ST-002',
    name: 'Upload Path Generation',
    description: 'Verify storage paths follow pattern',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async () => {
      // Test path patterns
      const woId = 'WO-12345';
      const timestamp = Date.now();
      const expectedPattern = `receipts/${woId}/${timestamp}.jpg`;

      if (!expectedPattern.match(/^receipts\/WO-\d+\/\d+\.jpg$/)) {
        return { status: 'fail', message: 'Path pattern invalid' };
      }

      return { status: 'pass', message: 'Path pattern correct: receipts/{woId}/{timestamp}.ext' };
    },
  },
  {
    id: 'ST-003',
    name: 'isOnline Detection',
    description: 'Verify navigator.onLine accuracy',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async () => {
      const isOnline = navigator.onLine;

      // Can't verify accuracy, but can check it exists
      if (typeof isOnline !== 'boolean') {
        return { status: 'fail', message: 'navigator.onLine not available' };
      }

      return {
        status: 'pass',
        message: `Browser reports: ${isOnline ? 'online' : 'offline'}`,
      };
    },
  },
  {
    id: 'ST-004',
    name: 'Photo Queue Limit',
    description: 'Verify MAX_QUEUED_PHOTOS (5) is enforced',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { maxPhotos, canQueuePhoto, pendingPhotoCount } = context.offline || {};

      if (maxPhotos === undefined) {
        return { status: 'warn', message: 'Offline context not available' };
      }

      if (maxPhotos !== 5) {
        return { status: 'warn', message: `MAX_QUEUED_PHOTOS is ${maxPhotos}, expected 5` };
      }

      return {
        status: 'pass',
        message: `Photo queue: ${pendingPhotoCount}/${maxPhotos}, canQueue=${canQueuePhoto}`,
      };
    },
  },
  {
    id: 'ST-005',
    name: 'Form Queue Limit',
    description: 'Verify MAX_QUEUED_FORMS (10) is enforced',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { maxForms, canQueueForm, pendingFormCount } = context.offline || {};

      if (maxForms === undefined) {
        return { status: 'warn', message: 'Offline context not available' };
      }

      if (maxForms !== 10) {
        return { status: 'warn', message: `MAX_QUEUED_FORMS is ${maxForms}, expected 10` };
      }

      return {
        status: 'pass',
        message: `Form queue: ${pendingFormCount}/${maxForms}, canQueue=${canQueueForm}`,
      };
    },
  },
  {
    id: 'ST-006',
    name: 'Offline Sync Status',
    description: 'Check current sync status',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { syncStatus, isOnline, pendingPhotoCount, pendingFormCount } = context.offline || {};

      if (syncStatus === undefined) {
        return { status: 'warn', message: 'Offline context not available' };
      }

      const pending = (pendingPhotoCount || 0) + (pendingFormCount || 0);

      if (syncStatus === 'error') {
        return { status: 'warn', message: 'Sync has errors' };
      }

      if (pending > 0 && !isOnline) {
        return { status: 'warn', message: `${pending} items pending, offline` };
      }

      return {
        status: 'pass',
        message: `Sync: ${syncStatus}, ${pending} pending, ${isOnline ? 'online' : 'offline'}`,
      };
    },
  },
];

// ============================================
// TEST CATEGORY: API SOURCE VALIDATION
// ============================================

export const apiSourceValidationTests = [
  {
    id: 'API-001',
    name: 'AppFolio API Health',
    description: 'Verify AppFolio proxy endpoint is reachable',
    destructive: false,
    aiCost: false,
    timeout: 15000,
    run: async () => {
      try {
        const wos = await fetchWorkOrders({ status: 'Assigned' });
        return {
          status: 'pass',
          message: `AppFolio API reachable — ${wos.length} assigned WOs returned`,
        };
      } catch (error) {
        return { status: 'fail', message: `AppFolio API unreachable: ${error.message}` };
      }
    },
  },
  {
    id: 'API-002',
    name: 'WO Status Distribution',
    description: 'Show real AppFolio status breakdown in loaded data',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders } = context.data;
      if (!workOrders?.length) return { status: 'warn', message: 'No work orders loaded' };

      const counts = {};
      workOrders.forEach(wo => {
        const s = wo.status || 'unknown';
        counts[s] = (counts[s] || 0) + 1;
      });

      const active = workOrders.filter(wo => isActiveWO(wo)).length;
      const completed = workOrders.filter(wo => isCompletedWO(wo)).length;
      const other = workOrders.length - active - completed;

      const details = Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([status, count]) => `${status}: ${count}`);

      return {
        status: 'pass',
        message: `${workOrders.length} WOs: ${active} active, ${completed} completed, ${other} other`,
        details,
      };
    },
  },
];

// ============================================
// TEST CATEGORY: CROSS-SCREEN CONSISTENCY
// ============================================
export const crossScreenConsistencyTests = [
  {
    id: 'CS-001',
    name: 'Leaderboard WO Count',
    description: 'Validates closed-this-month filter logic and shows count',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders, getLeaderboardData } = context.data;
      if (!workOrders?.length) return { status: 'warn', message: 'No work orders loaded' };

      // Replicate the leaderboard's closed-this-month filter
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const closedThisMonth = workOrders.filter(wo => {
        if (!isCompletedWO(wo)) return false;
        const d = wo.completedOn || wo.closedDate || wo.completedDate || wo.updatedDate;
        if (!d) return false;
        const date = new Date(d);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      });

      const details = [
        `Filter: completed status AND completed date in ${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`,
        `Total WOs: ${workOrders.length}`,
        `Closed this month: ${closedThisMonth.length}`,
      ];

      // Group by tech
      const byTech = {};
      closedThisMonth.forEach(wo => {
        const tech = wo.tech || 'Unassigned';
        byTech[tech] = (byTech[tech] || 0) + 1;
      });
      const sorted = Object.entries(byTech).sort((a, b) => b[1] - a[1]);
      sorted.slice(0, 5).forEach(([tech, count]) => {
        details.push(`  ${tech}: ${count} closed`);
      });

      return {
        status: 'pass',
        message: `${closedThisMonth.length} WOs closed this month across ${sorted.length} techs`,
        details,
      };
    },
  },
  {
    id: 'CS-002',
    name: 'Locks Screen WO Count',
    description: 'Validates open-WOs filter used by locks screen',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders, getOpenWOsForTech, getCurrentTech } = context.data;
      if (!workOrders?.length) return { status: 'warn', message: 'No work orders loaded' };

      const allOpen = workOrders.filter(wo => isActiveWO(wo));
      const tech = typeof getCurrentTech === 'function' ? getCurrentTech() : null;
      const techOpen = typeof getOpenWOsForTech === 'function'
        ? getOpenWOsForTech(tech?.name)
        : [];

      const details = [
        `Filter: status=open`,
        `All open WOs: ${allOpen.length}`,
        `Open WOs for ${tech?.name || 'current tech'}: ${techOpen.length}`,
        `Locks screen shows all open WOs for the current tech`,
      ];

      return {
        status: 'pass',
        message: `${techOpen.length} open WOs for ${tech?.name || 'current tech'} (${allOpen.length} total open)`,
        details,
      };
    },
  },
  {
    id: 'CS-003',
    name: 'Consult WO Truncation',
    description: 'Detects if consult screen truncates WO list and warns when more exist',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { getOpenWOsForTech, getCurrentTech } = context.data;
      const tech = typeof getCurrentTech === 'function' ? getCurrentTech() : null;
      const openWOs = typeof getOpenWOsForTech === 'function'
        ? getOpenWOsForTech(tech?.name)
        : [];

      if (openWOs.length <= 5) {
        return {
          status: 'pass',
          message: `${openWOs.length} open WOs — all visible on consult screen (no truncation)`,
        };
      }

      return {
        status: 'warn',
        message: `${openWOs.length} open WOs — consult initially shows 5, remaining behind "View all" button`,
        details: [
          `${tech?.name || 'Current tech'} has ${openWOs.length} open WOs`,
          `Consult screen shows first 5 by default with "View all" expansion`,
          `This is expected behavior, not a bug`,
        ],
      };
    },
  },
  {
    id: 'CS-004',
    name: 'LockCodes vs Units Count',
    description: 'Explains the difference between lockCodes count and units collection count',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { lockCodes, units } = context.data;

      const lockCount = lockCodes?.length || 0;
      const unitCount = units?.length || 0;

      const details = [
        `lockCodes collection: ${lockCount} entries`,
        `units collection: ${unitCount} entries`,
        `"All units (${lockCount})" on locks screen comes from lockCodes, not units`,
      ];

      if (lockCount !== unitCount) {
        details.push(`Difference: ${Math.abs(lockCount - unitCount)} — these are different collections`);
        details.push(`lockCodes = lock code directory, units = unit directory from property mgmt`);
      }

      return {
        status: lockCount === unitCount ? 'pass' : 'warn',
        message: `lockCodes: ${lockCount}, units: ${unitCount}${lockCount !== unitCount ? ' — different collections, different counts expected' : ''}`,
        details,
      };
    },
  },
  {
    id: 'CS-005',
    name: 'Per-Tech WO Reconciliation',
    description: 'Shows all 3 screen counts side-by-side with filter explanations',
    destructive: false,
    aiCost: false,
    timeout: 5000,
    run: async (context) => {
      const { workOrders, getOpenWOsForTech, getCurrentTech } = context.data;
      if (!workOrders?.length) return { status: 'warn', message: 'No work orders loaded' };

      const tech = typeof getCurrentTech === 'function' ? getCurrentTech() : null;
      const techName = tech?.name || 'Unknown';

      // Leaderboard: closed this month
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const closedThisMonth = workOrders.filter(wo => {
        if (wo.tech !== techName) return false;
        if (!isCompletedWO(wo)) return false;
        const d = wo.completedOn || wo.closedDate || wo.completedDate || wo.updatedDate;
        if (!d) return false;
        const date = new Date(d);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      });

      // Locks: all open for tech
      const openWOs = typeof getOpenWOsForTech === 'function'
        ? getOpenWOsForTech(techName)
        : [];

      // Consult: first 5 of open (now with show-more)
      const consultVisible = Math.min(openWOs.length, 5);

      const details = [
        `Tech: ${techName}`,
        ``,
        `Leaderboard: ${closedThisMonth.length} (filter: closed WOs in ${currentYear}-${String(currentMonth + 1).padStart(2, '0')})`,
        `Locks screen: ${openWOs.length} (filter: all open WOs for tech)`,
        `Consult screen: ${consultVisible} initially shown of ${openWOs.length} (first 5 + "View all" button)`,
        ``,
        `These counts are DIFFERENT BY DESIGN — each screen uses a different filter.`,
      ];

      return {
        status: 'pass',
        message: `${techName}: Leaderboard=${closedThisMonth.length}, Locks=${openWOs.length}, Consult=${consultVisible}/${openWOs.length}`,
        details,
      };
    },
  },
];

// ============================================
// EXPORT ALL CATEGORIES
// ============================================
export const allTestCategories = [
  {
    id: 'data-integrity',
    name: 'Data Integrity',
    description: 'Schema validation and referential integrity',
    tests: dataIntegrityTests,
  },
  {
    id: 'api-source-validation',
    name: 'API Source Validation',
    description: 'Verify AppFolio API connectivity and data status distribution',
    tests: apiSourceValidationTests,
  },
  {
    id: 'cross-screen-consistency',
    name: 'Cross-Screen Consistency',
    description: 'Verify WO counts and filters across different screens',
    tests: crossScreenConsistencyTests,
  },
  {
    id: 'function-execution',
    name: 'Function Execution',
    description: 'Core function return values and behavior',
    tests: functionExecutionTests,
  },
  {
    id: 'permissions',
    name: 'Permission Enforcement',
    description: 'Role checks and write access control',
    tests: permissionTests,
  },
  {
    id: 'workflows',
    name: 'Workflow Dry-Run',
    description: 'Write operation validation (non-destructive)',
    tests: workflowTests,
  },
  {
    id: 'ai-operations',
    name: 'AI Operations',
    description: 'OpenAI integration tests (may incur costs)',
    tests: aiTests,
  },
  {
    id: 'storage-offline',
    name: 'Storage & Offline',
    description: 'Firebase Storage and offline queue tests',
    tests: storageTests,
  },
];

export default allTestCategories;
